import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { AbsoluteRouteContext } from './context';

export const AbsoluteRouteDetectorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentRoute, setCurrentRoute] = useState('');
  const [params, setParams] = useState<Record<string, any> | undefined>(undefined);
  const [currentRoutes, setCurrentRoutes] = useState<{ name: string; key: string }[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const allLabels = useRef<Record<string, string>>({});
  const allRoutes = useRef<{ name: string; key: string }[]>([]);

  const handleCurrentRoute = useCallback((routeKey: string) => {
    const route = allRoutes.current.find((testRoute) => testRoute.key === routeKey);

    if (route && route.name !== 'AppDrawer') {
      setCurrentRoute(route.name);
    }
  }, []);

  const handleSetCurrentRoutes = useCallback(
    (routesFromParams: { name: string; key: string }[]) => {
      const newRoutes = [
        ...allRoutes.current,
        ...routesFromParams
          .filter(
            (route) => !allRoutes.current.some((existingRoute) => existingRoute.name === route.name)
          )
          .map((route) => ({ name: route.name, key: route.key })),
      ];

      allRoutes.current = newRoutes;
      setCurrentRoutes(newRoutes);
    },
    []
  );

  const setCurrentLabels = useCallback((newLabels: Record<string, string>) => {
    const labelsToBeAdded = Object.entries(newLabels).filter(
      ([label]) => !allLabels.current[label]
    );

    if (labelsToBeAdded.length > 0) {
      allLabels.current = {
        ...allLabels.current,
        ...newLabels,
      };
      setLabels(allLabels.current);
    }
  }, []);

  const memoizedValue = useMemo(
    () => ({
      currentRoute,
      setCurrentRoute: handleCurrentRoute,
      currentRoutes,
      setCurrentRoutes: handleSetCurrentRoutes,
      params,
      setParams,
      labels,
      setLabels: setCurrentLabels,
    }),
    [
      currentRoute,
      currentRoutes,
      handleCurrentRoute,
      handleSetCurrentRoutes,
      labels,
      params,
      setCurrentLabels,
    ]
  );

  return (
    <AbsoluteRouteContext.Provider value={memoizedValue}>{children}</AbsoluteRouteContext.Provider>
  );
};
